import { useEffect, useState } from "react";
import api, { fmtDate, fmtMXN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, Upload, Download, Trash2, Plus, FileText, Receipt, Ruler } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  { value: "estimacion_caratula", label: "Estimación / Carátula" },
  { value: "croquis", label: "Croquis" },
  { value: "reporte_fotografico", label: "Reporte fotográfico" },
  { value: "bitacora", label: "Bitácora" },
  { value: "estado_cuenta", label: "Estado de cuenta" },
  { value: "oficio_ingreso", label: "Oficio de ingreso" },
  { value: "acuse", label: "Acuse de recibido" },
  { value: "autorizacion_excedentes", label: "Autorización de excedentes" },
  { value: "precios_extraordinarios", label: "Precios extraordinarios" },
  { value: "convenio", label: "Convenio" },
  { value: "otro", label: "Otro" },
];
const dtLabel = (v) => DOC_TYPES.find((d) => d.value === v)?.label || v;

export default function EstimacionDossier({ estimacion, onClose }) {
  const { user } = useAuth();
  const canManage = ["admin", "contador", "auxiliar"].includes(user?.role);
  const canUpload = canManage || ["residente", "supervisor"].includes(user?.role);
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [docType, setDocType] = useState("estimacion_caratula");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get(`/estimaciones/${estimacion.id}/dossier`).then((r) => setData(r.data))
    .catch(() => toast.error("No se pudo cargar el expediente"));
  useEffect(() => {
    load();
    if (canManage) api.get("/requirements/templates").then((r) => setTemplates(r.data.filter((t) => t.active))).catch(() => {});
    // eslint-disable-next-line
  }, [estimacion.id]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await api.post(`/estimaciones/${estimacion.id}/dossier/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento agregado al expediente");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/estimaciones/${estimacion.id}/dossier/documents/${d.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: d.content_type })), "_blank");
    } catch { toast.error("No se pudo descargar"); }
  };

  const removeDoc = async (d) => {
    if (!confirm(`¿Eliminar "${d.name}" del expediente?`)) return;
    try { await api.delete(`/estimaciones/${estimacion.id}/dossier/documents/${d.id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const createChecklist = async (templateId) => {
    if (!templateId) return;
    try {
      await api.post(`/estimaciones/${estimacion.id}/dossier/checklist`, { template_id: templateId });
      toast.success("Checklist de requisitos creado — gestiónalo en Expediente Digital → Trámites");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (!data) return null;
  const est = data.estimacion;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="panel w-full max-w-3xl p-5 space-y-4 max-h-[92vh] overflow-y-auto" data-testid="estimacion-dossier">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-slate-100">Expediente de la Estimación #{String(est.number).padStart(2, "0")}</div>
            <div className="text-[10px] font-mono text-slate-500 uppercase">
              {data.contract_number} · {fmtDate(est.period_start)} → {fmtDate(est.period_end)} · estado: {est.status}
            </div>
          </div>
          <button data-testid="est-dossier-close" onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="panel p-3"><div className="label-mono text-[10px]">Importe del periodo</div><div className="font-mono text-sm font-bold text-cyan-300">{fmtMXN(est.subtotal_period)}</div></div>
          <div className="panel p-3"><div className="label-mono text-[10px]">Neto a pagar</div><div className="font-mono text-sm font-bold text-slate-200">{fmtMXN(est.net_to_pay)}</div></div>
          <div className="panel p-3"><div className="label-mono text-[10px] flex items-center gap-1"><Ruler className="w-3 h-3" />Generadores</div><div className="font-mono text-sm font-bold text-slate-200" data-testid="est-dossier-gens">{data.generadores.length}</div></div>
          <div className="panel p-3"><div className="label-mono text-[10px] flex items-center gap-1"><Receipt className="w-3 h-3" />CFDI</div><div className="font-mono text-sm font-bold text-slate-200" data-testid="est-dossier-cfdis">{data.facturas.length}</div></div>
        </div>
        <div className="text-[10px] text-slate-500">Importes y vínculos en vivo desde los módulos originales — este expediente no duplica datos.</div>

        {data.generadores.length > 0 && (
          <div className="panel p-3">
            <div className="label-mono mb-2">// GENERADORES VINCULADOS (por referencia)</div>
            {data.generadores.map((g) => (
              <div key={g.id} className="text-xs text-slate-300 py-0.5 flex gap-2">
                <span className="font-mono text-cyan-300">{g.code || g.id.slice(0, 6)}</span>
                <span className="flex-1 truncate">{g.concept || g.description || ""}</span>
                <span className="font-mono text-slate-500 uppercase">{g.status}</span>
              </div>
            ))}
          </div>
        )}
        {data.facturas.length > 0 && (
          <div className="panel p-3">
            <div className="label-mono mb-2">// CFDI VINCULADOS (por referencia)</div>
            {data.facturas.map((f) => (
              <div key={f.id} className="text-xs text-slate-300 py-0.5 flex gap-2">
                <span className="font-mono text-cyan-300">{f.folio}</span>
                <span className="font-mono">{fmtMXN(f.total)}</span>
                {f.uuid && <span className="font-mono text-slate-500 truncate">UUID {String(f.uuid).slice(0, 8)}…</span>}
                <span className="flex-1" />
                <span className="font-mono text-slate-400 uppercase">{f.status}</span>
              </div>
            ))}
          </div>
        )}

        <div className="panel p-3">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="label-mono">// CHECKLIST DE REQUISITOS DE LA ESTIMACIÓN</div>
            {canManage && data.checklists.length === 0 && (
              <select data-testid="est-dossier-checklist-select" defaultValue="" onChange={(e) => createChecklist(e.target.value)} className="input-cyber text-xs">
                <option value="">+ Crear desde plantilla…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.validation_pending ? " ⚠ (pendiente de validación)" : ""}</option>)}
              </select>
            )}
          </div>
          {data.checklists.length === 0 && <div className="text-xs text-slate-500 italic">Sin checklist. Usa la plantilla IGIFE Estimación No. 1 (pendiente de validación administrativa).</div>}
          {data.checklists.map((cl) => (
            <div key={cl.id} className="flex items-center gap-3 py-1" data-testid={`est-checklist-${cl.id}`}>
              <span className="text-sm text-slate-200 flex-1 truncate">{cl.label}{cl.validation_pending && <span className="ml-2 text-[10px] text-amber-300">⚠ plantilla pendiente de validación</span>}</span>
              <div className="w-24 h-2 rounded-full overflow-hidden" style={{ background: "rgba(148,163,184,0.15)" }}>
                <div className="h-full" style={{ width: `${cl.progress.pct}%`, background: "var(--cyan)" }} />
              </div>
              <span className="font-mono text-xs text-cyan-300">{cl.progress.pct}%</span>
            </div>
          ))}
          {data.checklists.length > 0 && <div className="text-[10px] text-slate-500 mt-1">Gestiona los requisitos en la pestaña Expediente Digital → Trámites del contrato.</div>}
        </div>

        <div className="panel p-3">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="label-mono">// DOCUMENTOS DEL EXPEDIENTE</div>
            {canUpload && (
              <div className="flex items-center gap-2">
                <select data-testid="est-doc-type" value={docType} onChange={(e) => setDocType(e.target.value)} className="input-cyber text-xs">
                  {DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
                <label className="btn-ghost cursor-pointer px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5" style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}>
                  <Upload className="w-3.5 h-3.5" /> Subir
                  <input data-testid="est-doc-file" type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp,.xml" onChange={upload} disabled={uploading} />
                </label>
              </div>
            )}
          </div>
          {data.documentos.length === 0 && <div className="text-xs text-slate-500 italic">Sin documentos. Sube carátula, croquis, reporte fotográfico, bitácora, oficio de ingreso, acuse…</div>}
          {data.documentos.map((d) => (
            <div key={d.id} className="flex items-center gap-2 p-2 rounded-sm mb-1" style={{ background: "rgba(15,23,42,0.5)" }} data-testid={`est-doc-${d.id}`}>
              <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-200 truncate">{d.name}</div>
                <div className="text-[10px] font-mono text-slate-500">{dtLabel(d.doc_type)} · {fmtDate(d.created_at)} · {d.created_by}</div>
              </div>
              <button onClick={() => download(d)} className="text-cyan-300 hover:text-cyan-100"><Download className="w-4 h-4" /></button>
              {canManage && <button onClick={() => removeDoc(d)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
